import type {
	BoolQuery,
	OpenSearchQuery,
	QueryClause,
	SortClause,
} from '../types/query'

/**
 * Type guard to check if query has a proper BoolQuery structure.
 */
function isBoolQuery(q: BoolQuery | QueryClause | undefined): q is BoolQuery {
	return !!q && 'bool' in q
}

/**
 * Create base OpenSearch query structure
 * @param options - Initial size/from
 * @returns A new OpenSearchQuery with the provided pagination fields
 * @example createQuery({ size: 100, from: 0 })
 */
export function createQuery<TAggs = unknown>(
	options?: OpenSearchQuery<TAggs>,
): OpenSearchQuery<TAggs> {
	const query: OpenSearchQuery<TAggs> = {}

	if (options?.size !== undefined) {
		query.size = options.size
	}

	if (options?.from !== undefined) {
		query.from = options.from
	}

	return query
}

/**
 * Add filter clauses to the query (creates/extends bool.filter)
 * @param query - Existing query to extend
 * @param filters - Clauses to append to `bool.filter`
 * @returns Query with `bool.filter` extended
 * @example withFilters(query, [termFilter('status', 'active'), existsFilter('user')])
 */
export function withFilters<TAggs>(
	query: OpenSearchQuery<TAggs>,
	filters: (QueryClause | BoolQuery)[],
): OpenSearchQuery<TAggs> {
	const existingBool = isBoolQuery(query.query) ? query.query.bool : {}
	const existingFilters = existingBool.filter ?? []

	const filterArray = Array.isArray(existingFilters)
		? existingFilters
		: [existingFilters]

	return {
		...query,
		query: {
			bool: {
				...existingBool,
				filter: [...filterArray, ...filters] as BoolQuery['bool']['filter'],
			},
		},
	}
}

/**
 * Add sort clauses to the query
 * @param query - Existing query to extend
 * @param sort - Sort clauses
 * @returns Query with `sort` set
 * @example withSort(query, [sortBy('@timestamp', 'desc')])
 */
export function withSort<TAggs>(
	query: OpenSearchQuery<TAggs>,
	sort: SortClause[],
): OpenSearchQuery<TAggs> {
	return {
		...query,
		sort,
	}
}
